// Generación real para el flujo "imagen con modelo" del Asistente.
//
// Arma el MISMO payload design-modelo single que usa el modo "imagen con
// modelo" (InterfazV2.handleGenerateFinal, branch design-modelo) y le pega
// al mismo endpoint (`GENERATE_AD_V3_URL` vía `postGenerateAd`). Mantener
// en sync con ese branch si cambia el contrato del backend.
//
// El agente no tiene paso de Fondo (todavía), así que el background va en
// modo 'ia' (automático). Una imagen por pose: el backend genera N
// imágenes a partir del array `pose`.

import { camposDeFormatoPersonalizado, formatoEstandarDe } from '../../utils/formatoPersonalizado';
import {
  postGenerateAd,
  postGenerateAdAtlas,
  GenerateAdError,
  mapErrorTypeToMessage,
  type GenerateAdErrorType,
} from '../../lib/generateAdRequest';
import { generateVideoCreator } from '../../lib/videoCreator';
import { recordPromptHistory } from './promptHistory';
import {
  GENERATE_AD_V3_URL,
  GENERATE_AUTH_TOKEN,
  USE_ATLAS_BACKEND,
} from '../../config';
import { supabase } from '../../lib/supabase';
import { generateAudioCreator } from '../../lib/audioGenerate';
import { acotarDuracionDeMusica } from '../../utils/duracionDeMusica';
import { acotarDuracionDeEfecto } from '../../utils/duracionDeEfecto';
import {
  safeResolution,
  VIDEO_BASICA_MAX_S,
  VIDEO_PROFESIONAL_MAX_S,
} from '../../utils/credits';
import type { ModelSelection } from './ModelPicker';
import { awaitImageGeneration } from './imageGenerationRecovery';
import type { ReferenciaEtiquetada } from './referenciasDeGeneracion';
import type { ResolucionImagen } from '../../lib/resolucionImagen';
import type { ProveedorDeImagen } from '../../lib/proveedoresDeImagen';
import { pollGeneratedByExecutionId } from './pollGeneratedByExecutionId';

// Tope del poll de recuperación de VIDEO por execution_id (los videos tardan
// varios minutos — mismo orden que el modo influencer de InterfazV2).
// 35 min: cubre el tope del provider de Seedance 2.5 (~30 min desde
// 2026-08-31, la cola GPU de Enhancor puede demorar) más el margen de cola
// propia; el influencer de V2 usa 30 min propios (ADR-543).
export const VIDEO_POLL_TIMEOUT_MS = 35 * 60 * 1000;

const GENERAR_V2_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/generar_anuncio_v2';
// Voz por defecto (Argie · Español · femenina) si el agente no especifica otra.
const DEFAULT_VOICE_ID = 'arMlPrYpUo1XH5F2zM6R';

// Mismo mapeo slug → aspect ratio que InterfazV2. Exportado para el modo `replicar`
// de `/v1/images/edit`, que recibe el ratio y no el slug (ADR-2026-09-13-replicar-una-foto-tres-pipelines).
// Un formato personalizado se genera en su proporción estándar más parecida
// (ADR-2026-10-20-formatos-personalizados).
export const formatSlugToAspect = (slug: string | null): string => {
  switch (formatoEstandarDe(slug)) {
    case 'story':
      return '9:16';
    case 'square':
      return '1:1';
    case 'portrait':
      return '2:3';
    case 'banner':
      return '16:9';
    case 'editorial':
      return '3:2';
    case 'billboard':
      return '21:9';
    case 'post':
    default:
      return '4:5';
  }
};

/** Fondo elegido por el agente. Mapea a los mismos `background_mode` que usa
 *  el flujo manual (InterfazV2). Si se omite → fondo automático ('ia'). */
export type AgentBackground =
  | { mode: 'auto' }
  | { mode: 'blanco' }
  | { mode: 'biblioteca'; id: number }
  | { mode: 'guardado'; fileName: string }
  | { mode: 'descripcion'; text: string };

export interface AgentModeloConfig {
  /** id_auth del usuario. */
  userId: string;
  /** Ids de variante (productos elegidos). */
  variantIds: string[];
  /** Selección de modelo (auto / guardado / custom). */
  modelSelection: ModelSelection | null;
  /** Poses elegidas — una imagen por pose. */
  poseIds: string[];
  /** Slug de formato (post / square / story / ...). */
  formatSlug: string;
  /** Resolución de salida (1K/2K/4K), como en la libre: viaja como `image_size`. Sin
   *  dato, la base (ADR-2026-09-13-preferencias-de-generacion-en-el-menu-del-mas). */
  imageSize?: ResolucionImagen;
  /** Instrucción adicional opcional. */
  instruccion?: string;
  /** Fondo elegido. Si se omite → automático ('ia'). */
  background?: AgentBackground | null;
  /** id_company — fallback si el backend no lo devuelve. */
  companyId?: string | null;
  /** Id de correlación (UUID del front). Si viene junto con companyId, la
   *  generación corre con RECUPERACIÓN: carrera respuesta-del-webhook vs
   *  poll por execution_id (ver imageGenerationRecovery). */
  executionId?: string;
  /**
   * Motor de imagen elegido en el menú del "+", igual que en la libre
   * (ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario). Viaja como
   * `provider_override`. Sin dato corre la cadena de siempre.
   *
   * Está acá y no sólo en la libre porque la elección es del USUARIO y no del camino:
   * generar con producto y modelo salía por la cadena por defecto aunque hubiera elegido
   * otro motor, sin nada que se lo dijera.
   */
  providerOverride?: ProveedorDeImagen | null;
}

/** Mapea el fondo del agente a los campos de payload del backend — misma
 *  lógica que InterfazV2.handleGenerateFinal (branch design-modelo). */
function backgroundFields(bg: AgentBackground | null | undefined): Record<string, any> {
  if (!bg || bg.mode === 'auto') return { background_mode: 'ia' };
  if (bg.mode === 'blanco') return { background_mode: 'blanco', background_id: 5 };
  if (bg.mode === 'biblioteca')
    return { background_mode: 'elegido', background_id: bg.id };
  if (bg.mode === 'guardado')
    return { background_mode: 'guardado', background_file_name: bg.fileName };
  return { background_mode: 'descripcion', background_text: bg.text };
}

export async function generateModeloImage(
  config: AgentModeloConfig,
): Promise<{ imageIds: (number | string)[]; companyId: string }> {
  const {
    userId,
    variantIds,
    modelSelection,
    poseIds,
    formatSlug,
    instruccion,
    background,
  } = config;
  if (variantIds.length === 0) throw new Error('Falta el producto.');

  // Modelo: shape del payload según el modo de la selección.
  let modelo_mode: 'ia' | 'guardados' | 'personalizado' = 'ia';
  const guardadosFields: Record<string, any> = {};
  const customFields: Record<string, any> = {};
  if (modelSelection?.mode === 'preset') {
    modelo_mode = 'guardados';
    if (modelSelection.id) {
      guardadosFields.modelo_guardado_id = String(modelSelection.id);
      if (modelSelection.name)
        guardadosFields.modelo_guardado_nombre = modelSelection.name;
    }
  } else if (modelSelection?.mode === 'custom') {
    modelo_mode = 'personalizado';
    const f = modelSelection.fields;
    if (f.genero) customFields.genero = f.genero;
    if (f.edad) customFields.edad = f.edad;
    if (f.tipoCuerpo) customFields.tipo_cuerpo = f.tipoCuerpo;
    if (f.tonoPiel) customFields.tono_piel = f.tonoPiel;
    if (f.colorPelo) customFields.color_pelo = f.colorPelo;
    if (f.largoPelo) customFields.largo_pelo = f.largoPelo;
    if (f.descripcion) customFields.desc_modelo = f.descripcion;
  }

  // El backend nuevo (atlas-backend) exige `execution_id` (UUID) SIEMPRE —
  // n8n toleraba su ausencia usando su id interno. Si el caller no trae uno
  // (asistente sin companyId → corre sin recovery), generamos uno igual solo
  // para cumplir el contrato; la recuperación por poll sigue requiriendo
  // executionId + companyId del caller, como antes.
  const executionId = config.executionId ?? crypto.randomUUID();

  const requestData: Record<string, any> = {
    user_id: userId,
    variante_id: variantIds,
    mode: variantIds.length > 1 ? 'multiple' : 'single',
    modo: 'personalizado',
    // Trackeo: modo de la UI que originó la generación (ADR-511). Todas
    // las generaciones del Asistente viajan como 'asistente'.
    modo_utilizado: 'asistente',
    cantidad_imagenes: 1,
    Format: formatSlugToAspect(formatSlug),
    // Con un formato personalizado, su copia: la descarga recorta y convierte a eso.
    ...camposDeFormatoPersonalizado(formatSlug),
    ...(config.imageSize ? { image_size: config.imageSize } : {}),
    composicion: 'modelo',
    modelo_mode,
    ...customFields,
    ...guardadosFields,
    ...(poseIds.length > 0 ? { pose: poseIds } : {}),
    // Fondo: lo que eligió el agente (o automático si no eligió nada).
    ...backgroundFields(background),
    ...(instruccion && instruccion.trim()
      ? { instruccion_adicional: instruccion.trim() }
      : {}),
    ...(config.providerOverride ? { provider_override: config.providerOverride } : {}),
    new_ui: true,
    modo_pro: true,
    execution_id: executionId,
  };

  // Camino del asistente: la instrucción también va al historial reusable. El
  // otro punto de registro es `handleGenerateFinal` (modos clásicos).
  recordPromptHistory(config.companyId, 'instruccion', instruccion);

  const fetchPromise = postGenerateAd(requestData, {
    url: GENERATE_AD_V3_URL,
  }) as Promise<{
    imagenes_generadas_ids?: Array<number | string>;
    id_company?: string;
  }>;

  // Con executionId + company: carrera respuesta vs poll — si el fetch muere
  // (pestaña cerrada al medio no aplica acá, pero sí red caída / compu
  // dormida), el poll recupera la tanda desde la DB.
  if (config.executionId && config.companyId) {
    return await awaitImageGeneration({
      fetchPromise,
      executionId: config.executionId,
      companyId: config.companyId,
      expectedCount: Math.max(1, poseIds.length),
    });
  }

  const data = await fetchPromise;
  const imageIds = data?.imagenes_generadas_ids;
  if (!Array.isArray(imageIds) || imageIds.length === 0) {
    throw new Error('No se recibieron imágenes generadas.');
  }
  return { imageIds, companyId: data?.id_company || config.companyId || '' };
}

// ─── Flujo libre (prompt + imágenes) ───────────────────────────────────
// Genera UNA imagen a partir de un prompt libre (y opcionalmente imágenes
// de referencia), `modo: 'creativo_plus'`. Migrado al atlas-backend
// (`/v1/ads/generate` vía `postGenerateAdAtlas`, ADR-583) con `execution_id`
// SIEMPRE (el backend lo exige) y sin `user_id`. El flag se maneja acá
// porque el fallback n8n es el webhook V2 (`generar_anuncio_v2`), distinto
// del V3 que rutea `postGenerateAd`.

/** Tope de imágenes por tanda — mismo que el control +/- de InterfazV2
 *  (`AUTO_IMAGE_COUNT_MAX`). */
export const FREEFORM_MAX_CANTIDAD = 6;

export interface AgentFreeformConfig {
  /** id_auth del usuario. */
  userId: string;
  /** Prompt libre de la imagen a generar. */
  prompt: string;
  /** Slug de formato (post / square / story / ...). */
  formatSlug: string;
  /** Cuántas imágenes generar en la tanda. Default 1, clamped a
   *  [1, FREEFORM_MAX_CANTIDAD]. Ojo: las filas llegan de a una con gaps de
   *  30-60s, así que el poll cierra por conteo de `expectedCount`. */
  cantidad?: number;
  /**
   * Imágenes de referencia (firmadas) CON su tipo: el backend lo convierte en la
   * etiqueta que precede a cada imagen en el pedido al motor —"Producto 1:", "Pose de
   * referencia (…):"— igual que el modo Modelo
   * (ADR-2026-09-08-las-referencias-del-agente-viajan-etiquetadas).
   */
  referenceImages?: ReferenciaEtiquetada[];
  /** id_company — fallback si el backend no lo devuelve. */
  companyId?: string | null;
  /** Id de correlación (UUID del front). Junto con companyId habilita la
   *  recuperación por poll (requiere que el webhook etiquete las filas). */
  executionId?: string;
  /**
   * Resolución de salida (1K/2K/4K). Viaja como `image_size`, que el backend traduce a
   * `imageConfig.imageSize` y multiplica en los créditos
   * (ADR-2026-08-25-resolucion-de-generacion-2k-4k). Sin dato no se manda y el backend
   * usa su default (1K) — es lo que hizo siempre el modo directo del chat.
   */
  imageSize?: ResolucionImagen;
  /**
   * Etiqueta `modo_utilizado` de las filas (métricas por modo). Default `'asistente'`,
   * la de siempre del modo directo del chat; "Prompt libre" manda `'prompt_libre'`
   * (ADR-2026-09-07-modo-prompt-libre).
   */
  modoUtilizado?: string;
  /**
   * Motor de imagen elegido en el menú del "+"
   * (ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario; nació como la tuerca de las
   * cuentas dev en ADR-2026-09-12-gpt-image-2-5-en-el-harness-y-en-el-agente). Viaja como
   * `provider_override` y ya no es privilegio de `dev_tester`: mandarlo lo puede cualquier
   * cuenta con el Agente, y lo que decide el backend es si corre SIN red (dev) o primero
   * con la cadena de siempre atrás. Sin dato no se manda y corre la cadena.
   */
  providerOverride?: ProveedorDeImagen | null;
  /**
   * Lo que el AGENTE declaró de esta generación: si crea una imagen nueva, EDITA una
   * existente (cambiar un color, un fondo, una pose) o COMPONE (collage, pieza con texto
   * legible — ADR-2026-09-17-componer-va-por-el-motor-de-edicion), y si muestra mucha piel. Viajan
   * como `tarea` / `piel_expuesta`; el backend elige la cadena de proveedores con eso.
   * El agente no conoce ni nombra modelos
   * (ADR-2026-09-13-el-agente-elige-el-motor-por-la-tarea).
   */
  tarea?: 'crear' | 'editar' | 'componer' | 'explorar' | 'grafica' | 'cine';
  /** `conservar` | `fusionar` (ADR-2026-10-07-conservar-o-fusionar): con `fusionar` el
   *  backend pone Seedream primero. Viaja como `edicion`. */
  edicion?: 'conservar' | 'fusionar';
  pielExpuesta?: boolean;
  /**
   * Foto de catálogo en fondo blanco puro, como el modo Modelo con fondo "Blanco": viaja
   * como `background_mode: "blanco"` + temperatura neutra + esquema de luz; el backend
   * suma el bloque al prompt y corrige el punto blanco después de generar
   * (ADR-2026-09-13-el-agente-genera-en-fondo-blanco).
   */
  fondoBlanco?: boolean;
  luzFondoBlanco?: 'difusa' | 'dura';
  /**
   * Que el backend REDACTE el prompt antes de generar, con las reglas del agente, y declare
   * él la tarea, la piel, el fondo blanco y qué es cada foto subida. Lo manda el modo
   * Redes cuando el prompt no viene ya de "Mejorar prompt"
   * (ADR-2026-10-04-el-escritor-de-imagen-usa-las-reglas-del-agente). Viaja como
   * `escribir_prompt`.
   */
  escribirPrompt?: boolean;
  /**
   * Lo que había en la caja de Redes al generar (prompt original, adjuntos, ajustes). Viaja
   * como `receta_de_redes`; el backend guarda el body entero en `datos_generacion`, y de
   * ahí lo lee "Rehacer" (ADR-2026-10-04-rehacer-desde-la-cuadricula). El backend no lo usa.
   */
  receta?: unknown;
}

const clampCantidadFreeform = (n: number | undefined): number =>
  Math.max(1, Math.min(FREEFORM_MAX_CANTIDAD, Math.floor(n ?? 1)));

/** El body de `creativo_plus` — compartido por la generación que espera y la que no. */
const armarCuerpoFreeform = (
  config: AgentFreeformConfig,
  cantidad: number,
  executionId: string,
): Record<string, any> => ({
  user_id: config.userId,
  variante_id: [],
  modo: 'creativo_plus',
  modo_utilizado: config.modoUtilizado ?? 'asistente',
  cantidad_imagenes: cantidad,
  Format: formatSlugToAspect(config.formatSlug),
  ...camposDeFormatoPersonalizado(config.formatSlug),
  ...(config.imageSize ? { image_size: config.imageSize } : {}),
  ...(config.providerOverride ? { provider_override: config.providerOverride } : {}),
  ...(config.tarea ? { tarea: config.tarea } : {}),
  ...(config.edicion ? { edicion: config.edicion } : {}),
  ...(config.pielExpuesta ? { piel_expuesta: true } : {}),
  ...(config.escribirPrompt ? { escribir_prompt: true } : {}),
  ...(config.receta ? { receta_de_redes: config.receta } : {}),
  ...(config.fondoBlanco
    ? {
        background_mode: 'blanco',
        fondo_blanco_temperatura: 'neutro',
        fondo_blanco_iluminacion: config.luzFondoBlanco ?? 'difusa',
      }
    : {}),
  new_ui: true,
  prompt: config.prompt.trim(),
  ...(config.referenceImages && config.referenceImages.length > 0
    ? {
        // Sin `url` cuando no hay: una referencia de producto puede viajar SÓLO por id y
        // el backend la resuelve contra el catálogo —es lo que salva al producto recién
        // traído de Tiendanube, cuya foto todavía vive en el CDN de la tienda y haría
        // rebotar el pedido entero (ADR-2026-09-21-el-producto-recien-traido-viaja-por-
        // id-y-no-por-link). Para todos los demás no cambia nada: siempre traen url.
        reference_images: config.referenceImages.map((r) => ({
          ...(r.url ? { url: r.url } : {}),
          tipo: r.tipo,
          ...(r.productId ? { product_id: r.productId } : {}),
          ...(r.etiqueta ? { etiqueta: r.etiqueta } : {}),
          ...(r.mencion ? { mencion: r.mencion } : {}),
        })),
      }
    : {}),
  execution_id: executionId,
});

/**
 * Lanza UNA generación libre y vuelve apenas el backend la acepta, SIN esperar imágenes
 * (`wait: false`) — para los lotes del agente, que poleean todos sus `execution_id`
 * juntos (ADR-2026-09-08-el-chat-trabaja-en-lote). Tira `GenerateAdError` si el backend
 * rechaza el pedido (402 de saldo incluido: el lote lo usa para cortar lo que falta).
 */
export async function lanzarFreeformImage(
  config: AgentFreeformConfig & { executionId: string },
): Promise<void> {
  if (!config.prompt.trim()) throw new Error('Falta el prompt.');
  const body = {
    ...armarCuerpoFreeform(config, clampCantidadFreeform(config.cantidad), config.executionId),
    wait: false,
  };
  if (USE_ATLAS_BACKEND) {
    await postGenerateAdAtlas(body);
    return;
  }
  const res = await fetch(GENERAR_V2_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: GENERATE_AUTH_TOKEN },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API ${res.status}`);
}

export async function generateFreeformImage(
  config: AgentFreeformConfig,
): Promise<{ imageIds: (number | string)[]; companyId: string }> {
  const { prompt } = config;
  if (!prompt.trim()) throw new Error('Falta el prompt.');
  const cantidad = clampCantidadFreeform(config.cantidad);

  // El atlas-backend exige execution_id (uuid) SIEMPRE — si el caller no
  // trae uno (camino sin recovery), generamos el fallback (patrón ADR-571).
  const executionId = config.executionId ?? crypto.randomUUID();

  const body = armarCuerpoFreeform(config, cantidad, executionId);

  const doFetch = async () => {
    if (USE_ATLAS_BACKEND) {
      // Saca user_id y maneja 402 (billing) / 202 (pending → decide el
      // poll cuando hay recovery) / 400 validation / shape V3.
      return await postGenerateAdAtlas(body);
    }
    const res = await fetch(GENERAR_V2_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`API ${res.status}`);
    return (await res.json()) as {
      response?: string;
      imagenes_generadas_ids?: Array<number | string>;
      id_company?: string;
    };
  };

  // Con executionId + company: carrera respuesta vs poll por execution_id.
  if (config.executionId && config.companyId) {
    return await awaitImageGeneration({
      fetchPromise: doFetch(),
      executionId: config.executionId,
      companyId: config.companyId,
      expectedCount: cantidad,
    });
  }

  const data = await doFetch();
  if (
    data?.response !== 'success' ||
    !Array.isArray(data?.imagenes_generadas_ids) ||
    data.imagenes_generadas_ids.length === 0
  ) {
    throw new Error('No se recibieron imágenes generadas.');
  }
  return {
    imageIds: data.imagenes_generadas_ids,
    companyId: data.id_company || config.companyId || '',
  };
}

// ─── Audio (voz / música) ──────────────────────────────────────────────
// Mismo flujo de audio que el Estudio de Video, vía el helper compartido
// `generateAudioCreator` (atlas `/v1/audio/generate` o n8n según flag,
// ADR-583). Texto→voz (TTS) o música. El backend escribe el archivo en el
// bucket `video-project-assets` y descuenta créditos solo. Devolvemos una
// URL firmada para reproducir el audio inline en el chat.

export interface AgentAudioConfig {
  userId: string;
  /** Texto a locutar (voz) o descripción de la música. */
  text: string;
  /** 'voz' (TTS, default), 'musica' o 'efecto' (efecto de sonido). */
  audioType?: 'voz' | 'musica' | 'efecto';
  /** id de voz (tabla `voces`). Default Argie. Solo para voz. */
  voiceId?: string;
  /** Velocidad de la voz (1.0 normal). */
  speed?: number;
  /** Solo música: instrumental sin letra. */
  instrumentalOnly?: boolean;
  /** Solo música: segundos (3 a 300; sin dato, 30). */
  durationSec?: number;
  projectId?: string | null;
}

export async function generateVoiceAudio(
  config: AgentAudioConfig,
): Promise<{ audioUrl: string; storagePath: string }> {
  const isMusic = config.audioType === 'musica';
  const isEfecto = config.audioType === 'efecto';
  if (!config.text.trim()) throw new Error('Falta el texto.');

  const body: Record<string, any> = {
    user_id: config.userId,
    ref_mode: isMusic ? 'musica' : isEfecto ? 'efecto' : 'voz',
    voz_id: isMusic || isEfecto ? '' : config.voiceId || DEFAULT_VOICE_ID,
    project_id: config.projectId || null,
    // Trackeo (ADR-585): origen de la generación. El backend de audio hoy NO
    // persiste este campo (schema sin modo_utilizado) — pedido al back; su
    // warn de unknown-keys lo va a mostrar hasta que lo sume.
    modo_utilizado: 'asistente_audio',
    text: config.text.trim(),
    instrumental_only: isMusic ? !!config.instrumentalOnly : false,
    ...(isMusic
      ? // ElevenLabs Music la respeta (ADR-2026-09-26-musica-con-elevenlabs-y-duracion).
        { duracion_seg: acotarDuracionDeMusica(config.durationSec) }
      : isEfecto
        ? // Sin duración pedida, la elige el modelo
          // (ADR-2026-09-27-efectos-de-sonido-con-elevenlabs).
          config.durationSec
          ? { duracion_seg: acotarDuracionDeEfecto(config.durationSec) }
          : {}
        : { velocidad_voz: config.speed ?? 1.0, dialogo_input_mode: 'texto' }),
  };

  // Helper con flag atlas/n8n (402 billing accionable, 500 tipado, timeout
  // de 5 min). Ver lib/audioGenerate.ts / ADR-583.
  const { path } = await generateAudioCreator(body);

  const { data } = await supabase.storage
    .from('video-project-assets')
    .createSignedUrl(path, 86400);
  if (!data?.signedUrl) throw new Error('No se pudo cargar el audio generado.');
  return { audioUrl: data.signedUrl, storagePath: path };
}

// ─── Video ──────────────────────────────────────────────────────────────
// Usa el MISMO flujo de video que el Director de Estudio, vía el helper
// compartido `generateVideoCreator` (atlas `/v1/videos/creator` o n8n según
// flag y ref_mode; ADR-584). Soporta primer/último frame e imágenes de
// referencia (todo por URL firmada) y dos calidades: básica (Seedance 2,
// hasta 15s) y profesional (Seedance 2.5, hasta 30s). El modo diálogo se
// quitó del asistente el 2026-08-30 (sigue vivo en el Estudio). Atlas
// responde `{response:'success', path}` (bucket `video-project-assets`); el
// n8n del asistente puede responder `video_id` — el id de la fila en
// `imagenes_generadas` (archivo en el bucket `generadas`). El agente arma
// las URLs de los frames antes de llamar (generando una imagen, o tomando
// la de un producto/modelo/adjunto).

export interface AgentVideoConfig {
  userId: string;
  /** Prompt de movimiento / escena del video. */
  prompt: string;
  formatSlug?: string;
  durationSeconds?: number;
  companyId?: string | null;
  projectId?: string | null;
  /**
   * Imágenes de referencia (URLs firmadas) CON su tipo — guían el look del video.
   *
   * Etiquetadas como las de imagen, pero el destino es otro: el proveedor de video toma
   * `images: string[]` y UN prompt, sin lugar para texto por imagen. Con el tipo, el
   * backend arma la leyenda `@Image1 es el producto…` que Seedance sí entiende, y le
   * manda las URLs planas en ESE orden
   * (ADR-2026-09-12-las-referencias-del-video-se-nombran-en-el-prompt).
   */
  referenceImages?: ReferenciaEtiquetada[];
  /**
   * El VIDEO a copiar editándolo (URL firmada de nuestro Storage, uno solo): viaja como
   * referencia y Seedance 2 lo reproduce cambiando sólo lo que pide el prompt. `desde` =
   * segundo del original donde arranca el tramo; el tramo dura `durationSeconds`
   * (ADR-2026-09-24-copiar-un-video-es-editarlo).
   */
  referenceVideos?: { url: string; desde?: number; modo?: 'editar' | 'copiar' | 'camara' | 'referencia' }[];
  /** URL firmada de la imagen a usar como PRIMER frame. */
  firstFrameUrl?: string | null;
  /** URL firmada de la imagen a usar como ÚLTIMO frame. */
  lastFrameUrl?: string | null;
  /** Calidad elegida en la card: 'profesional' (Seedance 2.5, hasta 30s —
   *  default) o 'basica' (Seedance 2, hasta 15s, más barata). */
  quality?: 'basica' | 'profesional';
  /** Con video de referencia, el usuario eligió la calidad en la card: el backend respeta
   *  `quality` en vez de la que pone el modo
   *  (ADR-2026-09-25-la-calidad-elegida-manda-con-video-de-referencia). */
  calidadElegida?: boolean;
  /** Resolución de salida (default 720p — lo que siempre mandó el asistente).
   *  Cambia la tarifa por segundo (básica 1/2/4 · profesional 2/3/5). */
  resolution?: '480p' | '720p' | '1080p';
  /** Id de correlación (UUID del front). Junto con companyId habilita la
   *  recuperación por poll — requiere que el webhook etiquete la fila de
   *  `imagenes_generadas` con este execution_id. */
  executionId?: string;
  /**
   * Etiqueta `modo_utilizado` de la fila (métricas por modo). Default
   * `'asistente_video'`, la de siempre del asistente; "Creador de video" manda
   * `'creador_video'` (ADR-2026-09-07-modo-creador-video).
   */
  modoUtilizado?: string;
  /**
   * Productos adjuntos como referencia, por id de variante — viaja como `variante_ids`,
   * el mismo contrato que el video con influencer, para que la fila del historial
   * linkee el producto y caiga en su marca (ADR-2026-09-13-brand-id-en-imagenes-generadas).
   * Sin esto Creador de video mandaba la FOTO del producto pero perdía su id.
   */
  variantIds?: string[];
  /**
   * Tipo de video del Creador ("general" o un slug de `video_tipos`). Enciende el escritor
   * de prompts del backend y, con referencias, permite prompt vacío
   * (ADR-2026-09-18-el-creador-de-video-escribe-el-prompt-con-skills-por-tipo).
   */
  tipoVideo?: string;
  /**
   * El prompt YA salió del escritor del backend (el botón "Mejorar prompt" de Redes) y el
   * usuario lo dejó igual o casi: viaja como `prompt_escrito` y el Creador no lo vuelve a
   * escribir (ADR-2026-10-04-mejorar-prompt-en-redes). Sólo tiene efecto con `tipoVideo`.
   */
  promptEscrito?: boolean;
  /** Ídem `AgentFreeformConfig.receta`: lo que había en la caja de Redes, para "Rehacer". */
  receta?: unknown;
}

export async function generateVideoFromPrompt(
  config: AgentVideoConfig,
): Promise<{ videoUrl: string; videoId: string | null }> {
  // Sin tipo (asistente) el prompt es obligatorio acá; con `tipoVideo` (el Creador) la
  // validación real es la de la ruta: exime si hay un tipo elegido o referencias
  // (ADR-2026-09-18-el-creador-de-video-escribe-el-prompt-con-skills-por-tipo).
  if (!config.prompt.trim() && !config.tipoVideo) throw new Error('Falta el prompt.');
  const formatSlug = config.formatSlug || 'story';
  // La calidad define el proveedor (Seedance 2 vs 2.5) y el tope de duración.
  // Default PROFESIONAL (2026-08-30). El clamp acá es el guard de verdad: la
  // card ya limita el slider, pero el agente puede mandar cualquier
  // `duration` en la tool call.
  const isPro = config.quality !== 'basica';
  const maxDur = isPro ? VIDEO_PROFESIONAL_MAX_S : VIDEO_BASICA_MAX_S;
  const dur = Math.min(maxDur, config.durationSeconds ?? 5);

  // `ref_mode`: el n8n bifurca por este campo — mismo contrato que el
  // Estudio (VideoEditor): none | reference | frames. Si hay frames va
  // 'frames' (aunque también haya referencias — el payload lleva ambos
  // campos igual); si hay solo referencias va 'reference'. El asistente ya
  // NO genera modo diálogo (se quitó 2026-08-30): una persona hablando se
  // pide como video normal con el habla descrita en el prompt.
  const hasReferences = !!(
    (config.referenceImages && config.referenceImages.length > 0) ||
    (config.referenceVideos && config.referenceVideos.length > 0)
  );
  const hasFrames = !!(config.firstFrameUrl || config.lastFrameUrl);
  const refMode = hasFrames ? 'frames' : hasReferences ? 'reference' : 'none';

  const body: Record<string, any> = {
    user_id: config.userId,
    company_id: config.companyId || null,
    project_id: config.projectId || null,
    prompt: config.prompt.trim(),
    duration: dur,
    aspect_ratio: formatSlugToAspect(formatSlug),
    quality: isPro ? 'profesional' : 'estandar',
    ...(config.calidadElegida ? { calidad_elegida: true } : {}),
    resolution: safeResolution(config.resolution),
    ref_mode: refMode,
    // Trackeo (ADR-585): video del asistente segmentado aparte de imagen.
    modo_utilizado: config.modoUtilizado ?? 'asistente_video',
    // Mismo shape que la generación de imagen. El ORDEN es el que numera la leyenda del
    // otro lado (`@Image1` es esta primera): no reordenar acá.
    ...(config.referenceImages && config.referenceImages.length > 0
      ? {
          reference_images: config.referenceImages.map((r) => ({
            ...(r.url ? { url: r.url } : {}),
            tipo: r.tipo,
            // Con el id y la vista, la leyenda del backend nombra las fotos de un mismo
            // producto como VISTAS de uno y no como productos distintos
            // (ADR-2026-09-24-el-video-elige-sus-referencias-por-importancia).
            ...(r.tipo === 'producto' && r.productId ? { product_id: r.productId } : {}),
            ...(r.tipo === 'producto' && r.vista ? { vista: r.vista } : {}),
            // Qué es la foto, dicho por el agente ("Pose: de perfil", "Estampa a copiar"):
            // la línea de referencias del prompt la nombra así, como el rótulo de imagen
            // (ADR-2026-09-24-las-referencias-del-video-llevan-la-etiqueta-del-agente).
            ...(r.etiqueta ? { etiqueta: r.etiqueta } : {}),
          })),
        }
      : {}),
    ...(config.referenceVideos && config.referenceVideos.length > 0
      ? { reference_videos: config.referenceVideos }
      : {}),
    ...(config.firstFrameUrl ? { first_frame: config.firstFrameUrl } : {}),
    ...(config.lastFrameUrl ? { last_frame: config.lastFrameUrl } : {}),
    ...(config.executionId ? { execution_id: config.executionId } : {}),
    // Los productos adjuntos por id, para que la fila linkee el producto y caiga en su
    // marca (ADR-2026-09-13-brand-id-en-imagenes-generadas). Mismo nombre que el
    // contrato del video con influencer.
    ...(config.variantIds && config.variantIds.length > 0
      ? { variante_ids: config.variantIds }
      : {}),
    // Tipo de video del Creador: enciende el escritor de prompts del backend
    // (ADR-2026-09-18-el-creador-de-video-escribe-el-prompt-con-skills-por-tipo).
    ...(config.tipoVideo ? { tipo_video: config.tipoVideo } : {}),
    ...(config.tipoVideo && config.promptEscrito ? { prompt_escrito: true } : {}),
    ...(config.receta ? { receta_de_redes: config.receta } : {}),
  };

  // Camino directo: helper con flag atlas/n8n y ruteo parcial por ref_mode
  // → URL firmada del video (ADR-584). `videoId` es la fila de
  // imagenes_generadas (atlas la escribe para asistente_video desde
  // 2026-08-31; n8n siempre la escribió) — el modo directo navega a la
  // pantalla de generadas con ese id.
  // El helper clasifica: GenerateAdError = fallo DEFINITIVO del backend
  // (billing/500 tipado); Error plano = técnico (timeout/red/202 pending) —
  // en la carrera de abajo, solo el definitivo corta el poll.
  const doFetch = async (): Promise<{ videoUrl: string; videoId: string | null }> => {
    const creator = await generateVideoCreator(body);
    if (creator.pending) {
      // 202: el wait del backend venció; el video sigue generándose. Error
      // TÉCNICO a propósito: con recovery, decide el poll por execution_id.
      throw new Error('La generación de video tardó demasiado. Probá de nuevo.');
    }

    if (creator.videoId) {
      // Fila de imagenes_generadas (atlas asistente_video / n8n).
      const videoUrl = await fetchGeneratedVideoUrl(
        creator.videoId,
        config.companyId,
      );
      return { videoUrl, videoId: creator.videoId };
    }

    // `path` relativo a video-project-assets (atlas / formato viejo n8n).
    const { data } = await supabase.storage
      .from('video-project-assets')
      .createSignedUrl(creator.path!, 86400);
    if (!data?.signedUrl)
      throw new Error('No se pudo cargar el video generado.');
    return { videoUrl: data.signedUrl, videoId: null };
  };

  // Sin correlación: el fetch es la única fuente (comportamiento directo).
  if (!config.executionId || !config.companyId) return await doFetch();

  // Carrera respuesta-del-webhook vs poll por execution_id — misma idea que
  // awaitImageGeneration pero para UNA fila de video: si el fetch muere
  // (timeout de 5 min, red caída, compu dormida), el poll recupera el video
  // apenas el backend escribe la fila en `imagenes_generadas`.
  const executionId = config.executionId;
  const companyId = config.companyId;
  const ac = new AbortController();
  return await new Promise<{ videoUrl: string; videoId: string | null }>((resolve, reject) => {
    let done = false;
    const succeed = (v: { videoUrl: string; videoId: string | null }) => {
      if (done) return;
      done = true;
      ac.abort();
      resolve(v);
    };
    const failHard = (err: unknown) => {
      if (done) return;
      done = true;
      ac.abort();
      reject(err);
    };

    doFetch().then(succeed, (err) => {
      // Fallo DEFINITIVO del backend (GenerateAdError: billing / 500 tipado /
      // error explícito) → no hay video que esperar. Error técnico (timeout /
      // red / 202 pending) → no fatal; decide el poll.
      if (err instanceof GenerateAdError) failHard(err);
    });

    pollGeneratedByExecutionId({
      executionId,
      companyId,
      expectedCount: 1,
      timeoutMs: VIDEO_POLL_TIMEOUT_MS,
      signal: ac.signal,
    })
      .then(async (rows) => {
        const okRow = rows.find((r) => !r.has_error);
        if (okRow) {
          try {
            succeed({
              videoUrl: await fetchGeneratedVideoUrl(okRow.id, companyId),
              videoId: String(okRow.id),
            });
          } catch (e) {
            failHard(e);
          }
          return;
        }
        if (rows.length > 0) {
          // La fila has_error trae la taxonomía del backend: censura y timeout
          // tienen copy propio (accionable + "no se consumieron créditos") en
          // vez del genérico "falló" que hacía reintentar lo mismo a ciegas.
          const t = (rows[0].error_type as GenerateAdErrorType) || 'technical';
          failHard(new GenerateAdError(t, mapErrorTypeToMessage(t), rows[0].error_type));
        } else {
          failHard(
            new Error('La generación de video tardó demasiado. Probá de nuevo.'),
          );
        }
      })
      .catch(() => {
        /* el poll no lanza — por las dudas, no-op: decide el fetch */
      });
  });
}

// Resuelve la URL firmada del video a partir de su fila en
// `imagenes_generadas`. La fila puede tardar en tener `file_name` (el backend
// la completa con lag) → retry corto, mismo patrón que GeneratedVideoInline.
// Exportada: la usa también la reanudación post-recarga de la card de video.
export async function fetchGeneratedVideoUrl(
  videoId: number | string,
  fallbackCompanyId?: string | null,
): Promise<string> {
  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
  for (let attempt = 1; attempt <= 10; attempt++) {
    if (attempt > 1) await delay(2000);
    const { data, error } = await supabase
      .from('imagenes_generadas')
      .select('file_name, id_company')
      .eq('id', videoId)
      .maybeSingle();
    if (error || !data?.file_name) continue;

    const companyId = data.id_company || fallbackCompanyId;
    if (!companyId) break;
    const { data: signed } = await supabase.storage
      .from('generadas')
      .createSignedUrl(`${companyId}/full/${data.file_name}`, 86400);
    if (signed?.signedUrl) return signed.signedUrl;
  }
  throw new Error('No se pudo cargar el video generado.');
}
